import React, { useEffect } from 'react';
import { Platform, Pressable, Text, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../../../styles/theme';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { LibraryPickerPanel } from '../LibraryPickerPanel';
import { pickerSlotLabels } from '../libraryPicker';
import { rowOf } from '../constants';

interface Props {
  entries: RecipeIndexEntry[];
  /** The slot being filled, or null when the drawer was opened from the header. */
  placingFor: number | null;
  weekStart: string;
  onSelect: (recipeId: string) => void;
  /** Hovered recipe, previewed in the target slot. */
  onPreview: (recipeId: string | null) => void;
  onClose: () => void;
}

export function LibraryDrawer({ entries, placingFor, weekStart, onSelect, onPreview, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { context, placeOn } = pickerSlotLabels(placingFor, weekStart, t, i18n.language);

  // The preview lives in the grid; never leave it behind when the drawer goes.
  useEffect(() => () => onPreview(null), [onPreview]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // Capture phase: the search field has focus and would see the key first.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <>
      {/* Transparent: the week stays readable, and a click on it closes the drawer. */}
      <Pressable style={styles.scrim} onPress={onClose} />

      <View style={styles.drawer} testID="library-drawer">
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.title}>{t('thisWeek.library')}</Text>
            {!!context && (
              <View style={styles.contextPill} testID="library-context">
                <Feather name="calendar" size={12} color={Paprika.primary} />
                <Text style={styles.contextText}>{context}</Text>
              </View>
            )}
          </View>
          <Pressable
            style={styles.closeBtn}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
          >
            <Feather name="x" size={15} color={Paprika.textSecondary} />
          </Pressable>
        </View>

        <LibraryPickerPanel
          entries={entries}
          mealIndex={placingFor !== null && placingFor >= 0 ? rowOf(placingFor) : null}
          placeLabel={placeOn}
          autoFocus
          onSelect={onSelect}
          onPreview={placingFor !== null ? onPreview : undefined}
        />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
  },
  drawer: {
    position: 'absolute' as any,
    top: 0,
    right: 0,
    bottom: 0,
    width: 480,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.14)',
    shadowOffset: { width: -4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 20,
    elevation: 20,
    zIndex: 64,
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 14,
    gap: 10,
  },
  headerText: {
    flex: 1,
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 26,
    lineHeight: 30,
    color: Paprika.textPrimary,
  },
  contextPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Paprika.brandTint,
    borderRadius: 999,
    paddingHorizontal: 11,
    paddingVertical: 5,
  },
  contextText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    color: Paprika.primary,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
